import { useState } from "react";
import { Operator } from "@/api.types";
import {
  getMarkerSizesByZoom,
  shouldShowTooltip,
  shouldShowStatus,
} from "@/components/markers/marker.util";

interface OperatorMarkerProps {
  operator: Operator;
  onClick?: () => void;
  zoomLevel: number;
}

export function OperatorMarker({
  operator,
  onClick,
  zoomLevel,
}: OperatorMarkerProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const isOnDuty = operator.status === "ON_DUTY";
  const sizes = getMarkerSizesByZoom(zoomLevel);

  const getInitials = (user: Operator["user"]) => {
    // If we have both names, use their initials
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`;
    }

    // If we have just one name, use its first letter
    if (user.firstName) return user.firstName[0];
    if (user.lastName) return user.lastName[0];

    // If we have an email, use its first letter
    if (user.email) return user.email[0].toUpperCase();

    // If we have a phone number, use its first letter
    if (user.phone) return user.phone[0];

    // Ultimate fallback
    return "?";
  };

  const getDisplayName = (user: Operator["user"]) => {
    if (user.firstName && user.lastName)
      return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    if (user.lastName) return user.lastName;
    if (user.email) return user.email;
    return "Unknown Operator";
  };

  return (
    <button
      type="button"
      className={`relative group cursor-pointer transform hover:scale-110 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full ${
        !isOnDuty ? "opacity-75" : ""
      }`}
      onClick={onClick}
      aria-label={`${getDisplayName(operator.user)} - ${operator.status.replace("_", " ")}`}
    >
      {/* Pulsing effect only for ON_DUTY operators */}
      {isOnDuty && shouldShowStatus(zoomLevel) && (
        <>
          <div className="absolute -inset-2 rounded-full bg-green-500 opacity-20 animate-ping" />
          <div className="absolute -inset-2 rounded-full bg-green-500 opacity-20" />
        </>
      )}

      {/* Operator avatar or initials */}
      {operator.user.picture ? (
        <img
          src={operator.user.picture}
          alt={getDisplayName(operator.user)}
          className={`${sizes.container} rounded-full ${sizes.border} ${
            isOnDuty ? "border-green-500" : "border-gray-400"
          } ${
            isLoaded ? "opacity-100" : "opacity-0"
          } transition-opacity duration-200`}
          onLoad={() => setIsLoaded(true)}
          onError={(e) => {
            e.currentTarget.onerror = null;
            setIsLoaded(true);
          }}
        />
      ) : (
        <div
          role="img"
          aria-label={`${getDisplayName(operator.user)} initials`}
          className={`${sizes.container} rounded-full flex items-center justify-center text-white ${sizes.fontSize} font-medium ${sizes.border} ${
            isOnDuty
              ? "bg-blue-500 border-green-500"
              : "bg-gray-500 border-gray-400"
          }`}
        >
          {getInitials(operator.user)}
        </div>
      )}

      {/* Status indicator */}
      {shouldShowStatus(zoomLevel) && (
        <div
          role="status"
          aria-label={`Status: ${operator.status.replace("_", " ")}`}
          className={`absolute -bottom-1 -right-1 ${sizes.statusIndicator} rounded-full ${sizes.border} border-white ${
            operator.status === "ON_DUTY"
              ? "bg-green-500"
              : operator.status === "OFF_DUTY"
                ? "bg-gray-600"
                : "bg-red-500"
          }`}
        />
      )}

      {/* Hover tooltip */}
      {shouldShowTooltip(zoomLevel) && (
        <div
          role="tooltip"
          className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 hidden group-hover:block bg-white px-2 py-1 rounded shadow-lg text-sm whitespace-nowrap z-10"
        >
          {getDisplayName(operator.user)}
          <div className="text-xs text-gray-500">
            {operator.status.replace("_", " ")}
          </div>
        </div>
      )}
    </button>
  );
}
